<script setup>
import { Tag } from 'primevue';
import { computed } from 'vue';

const props = defineProps({
    // Individual notification object
    notification: {
        type: Object,
        default: () => null
    },
    // Array of notifications
    notifications: {
        type: Array,
        default: () => []
    },
    // Notification type for styling (error, warning, info, success)
    type: {
        type: String,
        default: 'info',
        validator: (value) => ['error', 'warning', 'info', 'success'].includes(value)
    },
    // Show/hide the notification
    show: {
        type: Boolean,
        default: true
    }
});

// Get notifications to display (either single notification or array)
const notificationsToShow = computed(() => {
    if (props.notification) {
        return [props.notification];
    }
    return props.notifications;
});

// Only the default icon varies per type now. Colour comes from the shared
// .banner-* classes in _utils.scss, which read the severity tokens directly and
// therefore need no light/dark branching here.
const defaultIcons = {
    error: 'pi-exclamation-triangle',
    warning: 'pi-exclamation-triangle',
    info: 'pi-info-circle',
    success: 'pi-check-circle'
};

const cardClasses = computed(
    () => `flex items-start gap-3 p-3 rounded-lg border banner-${props.type}`
);

// Get icon for notification
const getIcon = (notification) => {
    return notification.icon || defaultIcons[props.type];
};
</script>

<template>
    <div v-if="show && notificationsToShow.length > 0" class="space-y-3">
        <div v-for="(notification, index) in notificationsToShow" :key="index" :class="cardClasses">
            <i :class="`pi ${getIcon(notification)} banner-text text-lg flex-shrink-0 mt-0.5`"></i>

            <div class="flex-1 min-w-0">
                <!-- Title and Tag Row -->
                <div v-if="notification.title || notification.tag" class="flex items-center gap-2 mb-1">
                    <span v-if="notification.title" :class="`text-sm font-medium banner-text`">
                        {{ notification.title }}
                    </span>
                    <Tag v-if="notification.tag" :value="notification.tag.label || notification.tag"
                        :severity="notification.tag.severity || 'secondary'" />
                </div>

                <!-- Message -->
                <div v-if="notification.message" :class="`text-sm font-medium banner-text`">
                    {{ notification.message }}
                </div>

                <!-- Description -->
                <div v-if="notification.description" class="text-sm text-text-secondary">
                    {{ notification.description }}
                </div>
            </div>
        </div>
    </div>
</template>
